import { useCallback, useEffect, useRef, useState } from 'react';

// Pré-carrega uma lista de imagens (fotos do orçamento) em base64, mesmo motivo
// do useLogoPreloader: react-to-print clona o DOM num iframe e o iOS Safari tira
// o snapshot antes de imagens remotas carregarem -> PDF sai sem as fotos. Base64
// vai inline no nó clonado, sem depender de rede dentro do iframe.
//
// Uso:
//   const { imagesBase64, waitForReady } = useImagesPreloader(paths);
//   <img src={imagesBase64[path] || urlRemota} />   // fallback preserva o hoje
export const useImagesPreloader = (paths) => {
  const [imagesBase64, setImagesBase64] = useState({});
  const readyPromiseRef = useRef(Promise.resolve());

  // Chave estável para não refazer o fetch a cada render.
  const pathsKey = (paths || []).filter(Boolean).join('|');

  useEffect(() => {
    const list = pathsKey ? pathsKey.split('|') : [];
    if (list.length === 0) {
      setImagesBase64({});
      readyPromiseRef.current = Promise.resolve();
      return;
    }

    let cancelled = false;

    const loadOne = (path) => {
      const url = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/${path}`;
      return fetch(url)
        .then((res) => res.blob())
        .then(
          (blob) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            })
        )
        .then(async (dataUrl) => {
          try {
            const img = new Image();
            img.src = dataUrl;
            await img.decode();
          } catch {
            // decode() pode falhar em browser antigo; segue mesmo assim.
          }
          return [path, dataUrl];
        })
        .catch(() => [path, null]); // falhou: mantém fallback para a URL remota
    };

    readyPromiseRef.current = Promise.all(list.map(loadOne)).then((entries) => {
      if (cancelled) return;
      const map = {};
      entries.forEach(([path, dataUrl]) => {
        if (dataUrl) map[path] = dataUrl;
      });
      setImagesBase64(map);
    });

    return () => {
      cancelled = true;
    };
  }, [pathsKey]);

  const waitForReady = useCallback(
    (timeoutMs = 4000) =>
      Promise.race([
        readyPromiseRef.current,
        new Promise((resolve) => setTimeout(resolve, timeoutMs)),
      ]),
    []
  );

  return { imagesBase64, waitForReady };
};
